import { useEffect, useRef, useState } from "preact/hooks";
import { buildAppClipUrl } from "@core/hooks/useAppClipUrl";
import { QrCodeGenerator } from "@core/components/QrCodeGenerator";
import { Spinner } from "@core/components/Elements";
import { getExperienceMode } from "@core/utils/experienceMode";
import getUserDevice from "@core/utils/getUserDevice";
import type { ModelData } from "@core/content/types";
import viewInArIcon from "../assets/icons/view-in-ar-icon.svg";

interface ARButtonProps {
  model: ModelData;
  onLaunchAR: (model: ModelData) => void;
  onLaunchClick?: (model: ModelData) => void;
  isChecking: boolean;
  isCompatible: boolean;
  runtimeIsPreparing?: boolean;
  hideOnDesktop?: boolean;
}

export function ARButton({
  model,
  onLaunchAR,
  onLaunchClick,
  isChecking,
  isCompatible,
  runtimeIsPreparing = false,
  hideOnDesktop = false,
}: ARButtonProps) {
  const [showQRPopup, setShowQRPopup] = useState(false);
  const { device, isMobile } = getUserDevice();
  const isDesktop = !isMobile;
  const isIOS = device === 'iOS' || device === 'AppClip';
  const [isIOSWarmupLoading, setIsIOSWarmupLoading] = useState(isIOS);
  const qrContainerRef = useRef<HTMLDivElement>(null);
  const popupWidth = 250;
  const qrSize = 218;

  if (hideOnDesktop && isDesktop) {
    return null;
  }

  const handleClick = () => {
    if (isDesktop) {
      setShowQRPopup(true);
      return;
    }

    if (isIOS && isCompatible) {
      onLaunchClick?.(model);
      try {
        const u = new URL(window.location.href);
        u.hash = model.id;
        u.searchParams.set('fromAr', '1');
        window.history.pushState({}, '', u.toString());
      } catch {
        // ignore
      }
      const returnUrl = window.location.href;
      const isOnsite = window.location.pathname.startsWith('/onsite') || getExperienceMode() === 'onsite';
      const baseUrl = isOnsite
        ? `${window.location.origin}/onsite`
        : window.location.origin + window.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);
      const appClipParams: Record<string, string> = {};
      const keysToForward = ['config'];
      for (const key of keysToForward) {
        const value = searchParams.get(key);
        if (value !== null) appClipParams[key] = value;
      }
      window.location.href = buildAppClipUrl(model.id, {
        returnUrl,
        baseUrl,
        params: appClipParams,
      });
      return;
    }

    onLaunchClick?.(model);
    onLaunchAR(model);
  };

  const qrUrl = window.location.href;

  useEffect(() => {
    if (isMobile || !showQRPopup) {
      return;
    }

    const handleOutsideClick = (event: PointerEvent) => {
      if (!qrContainerRef.current?.contains(event.target as Node)) {
        setShowQRPopup(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);

    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick);
    };
  }, [isMobile, showQRPopup]);

  useEffect(() => {
    if (!isIOS) {
      setIsIOSWarmupLoading(false);
      return;
    }

    setIsIOSWarmupLoading(true);
    const timeoutId = window.setTimeout(() => {
      setIsIOSWarmupLoading(false);
    }, 2000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isIOS]);

  const isLoading = !isDesktop && (
    (isIOS && (isChecking || isIOSWarmupLoading)) ||
    (!isIOS && (runtimeIsPreparing || isChecking))
  );

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isLoading]);

  const isDisabled = !isDesktop && !isIOS && (runtimeIsPreparing || isChecking);
  const buttonStyle = "bg-black/20 border-2 border-white text-white";
  const disabledStyle = "bg-neutral-500/40 border-2 border-white/70 text-white/75 cursor-not-allowed opacity-60";

  return (
    <div ref={qrContainerRef} className="relative w-full max-w-[298px] md:max-w-[235px] h-[64px]">
      {isLoading ? (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner size="2md" variant="light" />
        </div>
      ) : (
        <div className="w-full h-full">
          <button
            onClick={handleClick}
            disabled={isDisabled}
            className={`w-full h-full px-6 font-display font-bold text-[20px] leading-none tracking-[0.1em] uppercase flex items-center justify-center gap-[20px] ${
              isDisabled
                ? disabledStyle
                : `${buttonStyle} hover:bg-black/30 active:bg-black/30`
            }`}
          >
            {isDesktop ? (
              <>
                <span>View in AR</span>
                <img
                  src={viewInArIcon}
                  alt=""
                  aria-hidden="true"
                  className="h-[35px] w-[21px] shrink-0"
                />
              </>
            ) : (
              <span>Launch</span>
            )}
          </button>
        </div>
      )}

      {isDesktop && (
        <div
          className={`absolute bottom-full left-1/2 mb-10 z-50 -translate-x-1/2 transform transition-all duration-300 ease-out ${
            showQRPopup
              ? "translate-y-0 opacity-100 pointer-events-auto"
              : "translate-y-5 opacity-0 pointer-events-none"
          }`}
        >
          <div
            className="bg-white rounded-[8px] flex flex-col items-center pt-1 px-1 pb-5"
            style={{
              width: `${popupWidth}px`,
              boxSizing: "border-box",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            <QrCodeGenerator value={qrUrl} fgColor="#000000" size={qrSize} />
            <p className="text-black text-center text-lg tracking-[1.76px] font-[500] mt-2 mb-1">
              Scan QR code
            </p>
          </div>
          <div
            className="absolute left-1/2 -translate-x-1/2 -bottom-[18px] w-0 h-0"
            style={{
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderTop: "18px solid white",
            }}
          />
        </div>
      )}
    </div>
  );
}
